import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
    Rocket, PhoneCall, UsersThree, CheckCircle, ArrowRight, ArrowLeft,
    Plus, Trash, SpinnerGap, X,
} from "@phosphor-icons/react";

const PROVIDER_SPECS = {
    twilio: {
        label: "Twilio",
        hint: "From your Twilio Console: create an API Key + a TwiML App, and have your voice number ready.",
        fields: [
            { key: "account_sid", label: "Account SID", placeholder: "ACxxxxxxxx" },
            { key: "auth_token", label: "Auth Token", placeholder: "Your auth token" },
            { key: "api_key_sid", label: "API Key SID", placeholder: "SKxxxxxxxx" },
            { key: "api_key_secret", label: "API Key Secret", placeholder: "API key secret" },
            { key: "twiml_app_sid", label: "TwiML App SID", placeholder: "APxxxxxxxx" },
            { key: "voice_number", label: "Voice Number (E.164)", placeholder: "+14155550100" },
        ],
    },
    plivo: {
        label: "Plivo",
        hint: "From your Plivo Console: copy your Auth ID & Auth Token and your Plivo voice number.",
        fields: [
            { key: "auth_id", label: "Auth ID", placeholder: "MAxxxxxxxx" },
            { key: "auth_token", label: "Auth Token", placeholder: "Your auth token" },
            { key: "voice_number", label: "Voice Number (E.164)", placeholder: "+14155550100" },
        ],
    },
    exotel: {
        label: "Exotel",
        hint: "From your Exotel dashboard: API Key, API Token, Account SID & subdomain, and your ExoPhone. Advanced VoIP settings can be added later in Settings.",
        fields: [
            { key: "api_key", label: "API Key", placeholder: "Your API key" },
            { key: "api_token", label: "API Token", placeholder: "Your API token" },
            { key: "account_sid", label: "Account SID", placeholder: "your-account-sid" },
            { key: "subdomain", label: "Subdomain", placeholder: "api.exotel.com" },
            { key: "voice_number", label: "ExoPhone (E.164)", placeholder: "+914471279999" },
        ],
    },
};
const PROVIDER_ORDER = ["twilio", "plivo", "exotel"];

const STEPS = ["Welcome", "Connect provider", "Invite team", "All set"];

const StepDots = ({ step }) => (
    <div className="flex items-center gap-2" data-testid="onboarding-progress">
        {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-2">
                <div className={`grid h-7 w-7 place-items-center rounded-full text-[11px] font-bold transition-all ${
                    i < step ? "bg-emerald-500 text-white" : i === step ? "bg-ink text-white" : "bg-stone-200 text-stone2"
                }`}>
                    {i < step ? <CheckCircle size={14} weight="fill" /> : i + 1}
                </div>
                <span className={`hidden text-[11px] font-semibold sm:inline ${i === step ? "text-ink" : "text-stone2"}`}>{label}</span>
                {i < STEPS.length - 1 && <div className={`h-0.5 w-8 ${i < step ? "bg-emerald-400" : "bg-stone-200"}`} />}
            </div>
        ))}
    </div>
);

export default function OnboardingWizard({ orgName, onClose }) {
    const [step, setStep] = useState(0);
    const [busy, setBusy] = useState(false);
    const [provider, setProvider] = useState("twilio");
    const [creds, setCreds] = useState({});
    const [members, setMembers] = useState([{ name: "", email: "", password: "", role: "agent" }]);

    const finish = async () => {
        setBusy(true);
        try {
            await api.post("/settings/onboarding-complete");
        } catch (e) {
            // Don't trap the user, but be honest: if it didn't persist, we'll
            // prompt again next visit.
            toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save setup status — we'll prompt you again next time.");
        }
        setBusy(false);
        onClose?.();
    };

    // Closing the wizard (X) only hides it for this session — it does NOT mark
    // onboarding complete, so the Dashboard re-shows it on the next visit until
    // the admin actually finishes.
    const dismiss = () => onClose?.();

    const saveProvider = async () => {
        const spec = PROVIDER_SPECS[provider];
        const missing = spec.fields.filter((f) => !creds[f.key]?.trim());
        if (missing.length) { toast.error(`Please fill all ${spec.label} fields, or skip for now`); return; }
        setBusy(true);
        try {
            await api.post("/settings/providers", { name: provider, credentials: creds, enabled: true });
            await api.patch("/settings", {
                active_provider: provider,
                caller_ids: [{ label: "Primary line", number: creds.voice_number.trim(), is_default: true }],
            });
            toast.success(`${spec.label} connected — you can now make & receive calls`);
            setStep(2);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || `Could not validate ${spec.label} credentials`);
        } finally {
            setBusy(false);
        }
    };

    const inviteTeam = async () => {
        const toInvite = members.filter((m) => m.name.trim() && m.email.trim() && m.password.trim());
        if (toInvite.length === 0) { setStep(3); return; }
        setBusy(true);
        let ok = 0;
        for (const m of toInvite) {
            try {
                await api.post("/users", { name: m.name.trim(), email: m.email.trim(), password: m.password, role: m.role });
                ok += 1;
            } catch (e) {
                toast.error(`${m.email}: ${formatApiError(e.response?.data?.detail) || "could not invite"}`);
            }
        }
        setBusy(false);
        if (ok > 0) toast.success(`${ok} team member${ok > 1 ? "s" : ""} invited`);
        setStep(3);
    };

    const updateMember = (i, patch) => setMembers((m) => m.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
    const addMember = () => setMembers((m) => [...m, { name: "", email: "", password: "", role: "agent" }]);
    const removeMember = (i) => setMembers((m) => m.filter((_, idx) => idx !== i));

    return (
        <div className="fixed inset-0 z-[120] grid place-items-center bg-ink/60 p-4 backdrop-blur-sm" data-testid="onboarding-wizard">
            <div className="relative w-full max-w-2xl overflow-hidden rounded-3xl border border-stone-200 bg-paper shadow-2xl">
                <button
                    data-testid="onboarding-close"
                    onClick={dismiss}
                    className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-full text-stone2 transition-all hover:bg-stone-100"
                    title="Close — we'll remind you next time"
                >
                    <X size={16} weight="bold" />
                </button>

                <div className="border-b border-stone-100 px-8 pt-8 pb-5">
                    <StepDots step={step} />
                </div>

                <div className="px-8 py-7">
                    {step === 0 && (
                        <div data-testid="onboarding-step-welcome">
                            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-brand/10 text-brand"><Rocket size={24} weight="fill" /></div>
                            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight">Welcome to Inspiro Connect{orgName ? `, ${orgName}` : ""}.</h2>
                            <p className="mt-2 text-sm text-stone2">Let's get your workspace ready in 3 quick steps — connect your phone provider, invite your team, and make your first call.</p>
                            <ul className="mt-5 space-y-2 text-sm">
                                <li className="flex items-center gap-2"><PhoneCall size={16} weight="fill" className="text-brand" /> Connect your provider (Twilio, Plivo or Exotel) to make &amp; receive calls</li>
                                <li className="flex items-center gap-2"><UsersThree size={16} weight="fill" className="text-brand" /> Invite agents & supervisors</li>
                                <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-brand" /> Place a test call from the dialer</li>
                            </ul>
                        </div>
                    )}

                    {step === 1 && (
                        <div data-testid="onboarding-step-provider">
                            <h2 className="font-heading text-2xl font-bold tracking-tight">Connect your phone provider</h2>
                            <p className="mt-1 text-sm text-stone2">Choose your telephony provider and enter its credentials. Stored encrypted.</p>
                            <div className="mt-4 flex flex-wrap gap-2" data-testid="onboarding-provider-picker">
                                {PROVIDER_ORDER.map((p) => (
                                    <button
                                        key={p}
                                        type="button"
                                        data-testid={`onboarding-provider-${p}`}
                                        onClick={() => { setProvider(p); setCreds({}); }}
                                        className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all ${provider === p ? "border-brand bg-brand/10 text-brand" : "border-stone-300 text-stone2 hover:border-ink"}`}
                                    >
                                        {PROVIDER_SPECS[p].label}
                                    </button>
                                ))}
                            </div>
                            <p className="mt-3 text-xs text-stone2">{PROVIDER_SPECS[provider].hint}</p>
                            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {PROVIDER_SPECS[provider].fields.map((f) => (
                                    <div key={f.key}>
                                        <label className="text-[11px] font-bold uppercase tracking-wider text-stone2">{f.label}</label>
                                        <input
                                            data-testid={`onboarding-cred-${f.key}`}
                                            type={f.key.includes("secret") || f.key.includes("token") ? "password" : "text"}
                                            value={creds[f.key] || ""}
                                            onChange={(e) => setCreds({ ...creds, [f.key]: e.target.value })}
                                            placeholder={f.placeholder}
                                            className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-ink"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div data-testid="onboarding-step-team">
                            <h2 className="font-heading text-2xl font-bold tracking-tight">Invite your team</h2>
                            <p className="mt-1 text-sm text-stone2">Add agents and supervisors. They'll sign in with this email & password. You can always do this later in Settings.</p>
                            <div className="mt-5 space-y-3">
                                {members.map((m, i) => (
                                    <div key={i} className="grid grid-cols-12 gap-2" data-testid={`onboarding-member-${i}`}>
                                        <input value={m.name} onChange={(e) => updateMember(i, { name: e.target.value })} placeholder="Name" className="col-span-3 rounded-lg border border-stone-300 px-2.5 py-2 text-sm outline-none focus:border-ink" data-testid={`onboarding-member-name-${i}`} />
                                        <input value={m.email} onChange={(e) => updateMember(i, { email: e.target.value })} placeholder="email@company.com" className="col-span-4 rounded-lg border border-stone-300 px-2.5 py-2 text-sm outline-none focus:border-ink" data-testid={`onboarding-member-email-${i}`} />
                                        <input value={m.password} onChange={(e) => updateMember(i, { password: e.target.value })} type="password" placeholder="Temp password" className="col-span-2 rounded-lg border border-stone-300 px-2.5 py-2 text-sm outline-none focus:border-ink" data-testid={`onboarding-member-password-${i}`} />
                                        <select value={m.role} onChange={(e) => updateMember(i, { role: e.target.value })} className="col-span-2 rounded-lg border border-stone-300 bg-white px-2 py-2 text-sm outline-none focus:border-ink" data-testid={`onboarding-member-role-${i}`}>
                                            <option value="agent">Agent</option>
                                            <option value="supervisor">Supervisor</option>
                                        </select>
                                        <button onClick={() => removeMember(i)} className="col-span-1 grid place-items-center rounded-lg border border-stone-200 text-stone2 hover:border-red-300 hover:text-red-500" data-testid={`onboarding-member-remove-${i}`}>
                                            <Trash size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button onClick={addMember} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline" data-testid="onboarding-add-member">
                                <Plus size={14} weight="bold" /> Add another
                            </button>
                        </div>
                    )}

                    {step === 3 && (
                        <div data-testid="onboarding-step-done" className="text-center">
                            <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-100 text-emerald-600"><CheckCircle size={30} weight="fill" /></div>
                            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight">You're all set! 🎉</h2>
                            <p className="mt-2 text-sm text-stone2">Your workspace is ready. Open the dialer, set yourself <strong>Online</strong>, and place a test call to confirm everything works end to end.</p>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between border-t border-stone-100 px-8 py-5">
                    <div>
                        {step > 0 && step < 3 && (
                            <button onClick={() => setStep(step - 1)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-stone2 hover:text-ink" data-testid="onboarding-back">
                                <ArrowLeft size={14} weight="bold" /> Back
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        {(step === 1 || step === 2) && (
                            <button onClick={() => setStep(step + 1)} className="text-sm font-medium text-stone2 hover:text-ink" data-testid="onboarding-skip">
                                Skip for now
                            </button>
                        )}
                        {step === 0 && (
                            <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 font-heading text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-hover" data-testid="onboarding-start">
                                Get started <ArrowRight size={14} weight="bold" />
                            </button>
                        )}
                        {step === 1 && (
                            <button disabled={busy} onClick={saveProvider} className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 font-heading text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-hover disabled:opacity-50" data-testid="onboarding-save-provider">
                                {busy ? <SpinnerGap size={14} className="animate-spin" /> : <>Connect & continue <ArrowRight size={14} weight="bold" /></>}
                            </button>
                        )}
                        {step === 2 && (
                            <button disabled={busy} onClick={inviteTeam} className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 font-heading text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-hover disabled:opacity-50" data-testid="onboarding-invite-team">
                                {busy ? <SpinnerGap size={14} className="animate-spin" /> : <>Continue <ArrowRight size={14} weight="bold" /></>}
                            </button>
                        )}
                        {step === 3 && (
                            <button disabled={busy} onClick={finish} className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-2.5 font-heading text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50" data-testid="onboarding-finish">
                                {busy ? <SpinnerGap size={14} className="animate-spin" /> : "Go to dashboard"}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
